/**
 * As fotografias e a conferência facial do caso, na pré-análise (E123.F3, GUS-128, ADR-0079 E.24).
 * Regra pura, sem I/O: o estado da seção e a origem de cada fotografia escolhida.
 *
 * A etapa "Análise fotográfica" saiu da esteira: o sistema classifica as imagens do material, elege
 * a fotografia do documento e a do titular e faz a conferência facial sozinho. A seção junta as
 * três leituras que já existiam — as fotos do caso, a conferência e as candidatas — e responde uma
 * pergunta só: em que ponto está a comparação, e o que falta para o perito?
 */
import {
  motivoSemVinculo,
  semRepetidas,
  type CandidatasDoCasoData,
  type MotivoSemVinculo,
} from '@/lib/candidatas_de_imagem';
import type { ConferenciaFacialData } from '@/lib/conferencia_facial';
import type {
  FotoDoSignatarioData,
  FotosDoSignatarioData,
  GrupoDaFoto,
  MotivoDaLeituraDoRecorte,
} from '@/lib/fotos_do_signatario';

export interface FotografiasDoCasoData {
  fotos: FotosDoSignatarioData;
  conferencia: ConferenciaFacialData;
  candidatas: CandidatasDoCasoData;
}

/**
 * - `conferindo`: a conferência automática está rodando.
 * - `realizada`: há parecer, e ele é sobre o par escolhido agora — as mesmas fotos, com a mesma
 *   caixa e o mesmo giro (E141.F2, ADR-0079 E.26).
 * - `pendente`: o par existe, mas o parecer dele não saiu (o serviço não respondeu, ou a troca ou
 *   o recorte ainda não foram conferidos) — a retaguarda tenta de novo, e o perito pode tentar agora.
 * - `organizando`: as imagens do material ainda estão sendo classificadas.
 * - `selecione`: há imagens de rosto no material, mas o sistema não formou o par (GUS-57, RN01).
 * - `sem_fotos`: o material não trouxe fotografia comparável (GUS-57, alteração 4).
 */
export type EstadoDasFotografias =
  | 'conferindo'
  | 'realizada'
  | 'pendente'
  | 'organizando'
  | 'selecione'
  | 'sem_fotos';

/** Alguma candidata que pode ser a fotografia do documento ou a do titular (ou ainda sem classe). */
function temImagemDeRosto(candidatas: CandidatasDoCasoData): boolean {
  return semRepetidas(candidatas.candidatas).some(
    (c) => c.classe !== 'outra' && c.classe !== 'identidade_verso',
  );
}

export function estadoDasFotografias(dados: FotografiasDoCasoData): EstadoDasFotografias {
  const { conferencia, candidatas } = dados;
  if (conferencia.em_curso === true) return 'conferindo';
  if (
    conferencia.parecer !== null &&
    !conferencia.par_mudou &&
    conferencia.recorte_mudou !== true
  ) {
    return 'realizada';
  }
  if (conferencia.tem_par_eleito) return 'pendente';
  if (candidatas.classificacao_em_curso === true) return 'organizando';
  return temImagemDeRosto(candidatas) ? 'selecione' : 'sem_fotos';
}

/**
 * Enquanto a classificação, a conferência ou a leitura de alguma imagem de documento rodam em
 * segundo plano, a seção relê. E124.F1 (GUS-134): a leitura também, porque desde o upload comum
 * ela pode terminar depois da classificação.
 */
export function precisaReler(dados: FotografiasDoCasoData | null): boolean {
  if (dados === null) return false;
  return (
    dados.conferencia.em_curso === true ||
    dados.candidatas.classificacao_em_curso === true ||
    (dados.candidatas.leituras_em_curso ?? []).length > 0
  );
}

/**
 * E124.F1 (GUS-134): a análise fotográfica ainda está sendo feita e o documento baixado agora sairia
 * sem ela — a conferência roda, ou as imagens ainda são organizadas sem par eleito. A emissão
 * avisa, sem travar; com o parecer, a pendência ou a limitação, não há o que esperar.
 */
export function analiseFotograficaEmAndamento(dados: FotografiasDoCasoData | null): boolean {
  if (dados === null) return false;
  const estado = estadoDasFotografias(dados);
  return estado === 'conferindo' || estado === 'organizando';
}

export function eleitaDoGrupo(
  fotos: FotosDoSignatarioData,
  grupo: GrupoDaFoto,
): FotoDoSignatarioData | null {
  const doGrupo = fotos.grupos.find((g) => g.grupo === grupo);
  if (doGrupo === undefined || doGrupo.eleita_id === null) return null;
  return doGrupo.fotos.find((f) => f.id === doGrupo.eleita_id) ?? null;
}

/** De qual documento e página a fotografia saiu; `null` na anexada à mão ou sem o documento. */
export function origemDaFoto(
  foto: FotoDoSignatarioData,
  candidatas: CandidatasDoCasoData,
): { documento: string; pagina: number | null } | null {
  const proveniencia = foto.proveniencia ?? null;
  if (proveniencia === null) return null;
  const nome =
    candidatas.candidatas.find((c) => c.documento_id === proveniencia.documento_id)
      ?.documento_nome ??
    candidatas.leituras?.find((l) => l.documento_id === proveniencia.documento_id)?.documento_nome;
  return nome === undefined ? null : { documento: nome, pagina: proveniencia.pagina };
}

/**
 * E162.F1.T08 (ADR-0111 §2.4, QA-25): a fotografia escolhida saiu de um documento sem vínculo
 * demonstrado com o contrato e a pessoa do caso — ela não corrobora a autoria. O vínculo é o da
 * candidata daquele documento; `null` na anexada à mão, com vínculo ou sem apuração.
 */
export function motivoSemVinculoDaFoto(
  foto: FotoDoSignatarioData,
  candidatas: CandidatasDoCasoData,
): MotivoSemVinculo | null {
  const proveniencia = foto.proveniencia ?? null;
  if (proveniencia === null) return null;
  const candidata = candidatas.candidatas.find((c) => c.documento_id === proveniencia.documento_id);
  return candidata === undefined ? null : motivoSemVinculo(candidata.vinculo);
}

/**
 * E141.F2 (ADR-0079 E.26): a leitura automática do recorte não produziu caixa (ou falhou) e o perito
 * ainda não ajustou — a imagem entrou inteira na conferência. Espelha `recorte_pendente` do servidor:
 * a foto nunca lida e a recortada na tela não estão pendentes.
 */
export function recortePendente(foto: FotoDoSignatarioData): boolean {
  const leitura = foto.recorte_automatico ?? null;
  return leitura !== null && leitura.motivo !== null && leitura.ajustada_em === null;
}

/** O motivo da pendência do recorte, ou `null` quando não há pendência. */
export function motivoDaPendencia(foto: FotoDoSignatarioData): MotivoDaLeituraDoRecorte | null {
  return recortePendente(foto) ? (foto.recorte_automatico?.motivo ?? null) : null;
}

/** Alguma das fotografias escolhidas agora foi escolhida pelo sistema. */
export function escolhidaPeloSistema(fotos: FotosDoSignatarioData): boolean {
  return fotos.grupos.some((g) => {
    const eleita = g.fotos.find((f) => f.id === g.eleita_id);
    return eleita?.confirmacao_automatica === true;
  });
}
